import '../styles.scss';

import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Alert, Box, Button, Text, Title } from '@gjensidige/builders-components';

import { postSportInjury } from '../api';
import { Datepicker, Dropdown, Input, InputGroup, Textarea } from '../components';
import {
  forbundOptions,
  jaNeiOptions,
  skadetKroppsdelOptions,
  typeUnderlagOptions,
} from '../constants';
import { fylkeOptions } from '../constants';
import { texts } from '../texts';
import { getEndOfToday } from '../utils/date';
import { SportInjury, sportInjuryForm } from '../utils/types';

const SportInjuryForm = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SportInjury>({
    resolver: zodResolver(sportInjuryForm),
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [errorSubmitting, setErrorSubmitting] = useState<boolean>(false);

  const onSubmit: SubmitHandler<SportInjury> = (data) => {
    setIsSubmitting(true);
    setErrorSubmitting(false);
    postSportInjury(data)
      .then((response) => {
        if (response === 'sent') {
          setIsSubmitting(false);
          setSuccess(true);
        }
      })
      .catch(() => {
        setIsSubmitting(false);
        setErrorSubmitting(true);
      });
  };
  return (
    <form
      method="post"
      onSubmit={(e) => {
        e.preventDefault();
        setIsSubmitting(true);
        handleSubmit(onSubmit);
      }}
      className="schema"
    >
      <Text className="builders-margin-bottom-20">MELD SKADE</Text>
      <Title as="h1" size="2" className="builders-margin-bottom-40">
        Skademeldingsskjema for idrettsskade
      </Title>
      <Text className="builders-margin-bottom-10">* Påkrevde felter</Text>
      <Box variant="form-section" className="builders-margin-bottom-10">
        <Dropdown
          name="f01_forbund"
          labelText="Forbund *"
          placeholder="Velg..."
          options={forbundOptions}
          control={control}
          error={errors.f01_forbund}
          tracking={false}
        />
        <Input
          name="f02_klubb"
          labelText="Klubb"
          control={control}
          error={errors.f02_klubb}
          tracking={false}
        />
        <Dropdown
          name="f03_fylke"
          labelText="Fylke *"
          placeholder="Velg..."
          options={fylkeOptions}
          control={control}
          error={errors.f03_fylke}
          tracking={false}
        />
      </Box>
      <Box variant="form-section" className="builders-margin-bottom-80">
        <InputGroup
          name="f04_idrettsforsikring"
          control={control}
          labelText="Hvilken type idrettsforsikring har du?"
          options={[
            { label: 'Grunnlisens', value: 'Grunnlisens' },
            { label: 'Utvidet lisens', value: 'Utvidet lisens' },
          ]}
          error={errors.f04_idrettsforsikring}
          type="radio"
          variant="column"
          tracking={false}
        />
      </Box>
      <Title as="h2" size="4" className="builders-margin-bottom-20">
        Informasjon om utøver
      </Title>
      <Box variant="form-section" className="builders-margin-bottom-80">
        <Input
          name="f05_skadelidte"
          labelText="For- og etternavn (skadelidte) *"
          control={control}
          error={errors.f05_skadelidte}
          tracking={false}
        />
        <Input
          name="f06_skadelidteFodselsdato"
          labelText="Fødselsdato- og -år (dd.mm.åååå) *"
          control={control}
          error={errors.f06_skadelidteFodselsdato}
          tracking={false}
          placeholder="dd.mm.åååå"
          helpText="Vennligst ikke oppgi fødselsnummer."
        />
        <Input
          name="f07_foresatt"
          labelText="For- og etternavn (foresatt) *"
          control={control}
          error={errors.f07_foresatt}
          tracking={false}
        />
        <Input
          name="f08_foresattFodselsdato"
          labelText="Fødselsdato- og -år på foresatt (dd.mm.åååå) *"
          control={control}
          error={errors.f08_foresattFodselsdato}
          tracking={false}
          placeholder="dd.mm.åååå"
          helpText="Vennligst ikke oppgi fødselsnummer."
        />
        <Input
          name="f09_adresse"
          labelText="Adresse *"
          control={control}
          error={errors.f09_adresse}
          tracking={false}
        />
        <Input
          name="f10_postnummer"
          labelText="Postnummer *"
          control={control}
          error={errors.f10_postnummer}
          tracking={false}
        />
        <Input
          name="f11_poststed"
          labelText="Poststed *"
          control={control}
          error={errors.f11_poststed}
          tracking={false}
        />
        <Input
          name="f12_telefonnummer"
          labelText="Telefonnummer *"
          control={control}
          error={errors.f12_telefonnummer}
          tracking={false}
        />
        <Input
          name="f13_epostadresse"
          labelText="E-postadresse *"
          control={control}
          error={errors.f13_epostadresse}
          tracking={false}
        />
        <Input
          name="f14_kontonummer"
          labelText="Kontonummer dersom du får erstatning"
          control={control}
          error={errors.f14_kontonummer}
          tracking={false}
        />
      </Box>
      <Title as="h3" size="4" className="builders-margin-bottom-20">
        Informasjon om skaden
      </Title>
      <Box variant="form-section" className="builders-margin-bottom-10">
        <Datepicker
          name="f15_skadedato"
          control={control}
          labelText="Når skjedde skaden? Vennligst fyll in skadedato *"
          error={errors.f15_skadedato}
          max={getEndOfToday()}
          formatDayJs="DD.MM.YYYY"
          tracking={false}
        />
      </Box>
      <Box variant="form-section" className="builders-margin-bottom-10">
        <InputGroup
          name="f16_skadeKontekst"
          control={control}
          labelText="Skjedde skaden i tilknytning til"
          options={[
            { label: 'Kamp/konkurranse', value: 'Kamp/konkurranse' },
            { label: 'Under trening', value: 'Under trening' },
            { label: 'Annet', value: 'Annet' },
          ]}
          error={errors.f16_skadeKontekst}
          type="radio"
          variant="column"
          tracking={false}
        />
        <Input
          name="f17_skadeKontekstAnnet"
          labelText="Hvis annet, vennligst spesifiser"
          control={control}
          error={errors.f17_skadeKontekstAnnet}
          tracking={false}
        />
        <Textarea
          name="f18_skadeHvordan"
          labelText="Beskriv hvordan skaden oppstod: *"
          control={control}
          error={errors.f18_skadeHvordan}
          tracking={false}
          maxWarning={500}
        />
        <Input
          name="f19_skadeAkutt"
          labelText="Har skaden oppstått akutt eller kommet over tid? *"
          control={control}
          error={errors.f19_skadeAkutt}
          tracking={false}
        />
      </Box>
      <Box variant="form-section" className="builders-margin-bottom-80">
        <InputGroup
          name="f20_skadeKroppsdel"
          control={control}
          labelText="Skadet kroppsdel"
          options={skadetKroppsdelOptions}
          error={errors.f20_skadeKroppsdel}
          type="checkbox"
          variant="column"
          tracking={false}
        />
        <Textarea
          name="f21_skadeDetaljertBeskrivelse"
          labelText="Detaljert beskrivelse av skaden (mer detaljert på kroppsdel, høyre, venstre, symptomer/diagnose): *"
          control={control}
          maxWarning={500}
          error={errors.f21_skadeDetaljertBeskrivelse}
          tracking={false}
        />
      </Box>

      <Title as="h4" size="4" className="builders-margin-bottom-20">
        Tilleggsopplysninger
      </Title>
      <Box variant="form-section" className="builders-margin-bottom-80">
        <InputGroup
          name="f22_skadeUnderlag"
          control={control}
          labelText="På hvilket type underlag skjedde skaden?"
          options={typeUnderlagOptions}
          error={errors.f22_skadeUnderlag}
          type="radio"
          variant="column"
          tracking={false}
        />
        <InputGroup
          name="f23_beskyttelsesutstyr"
          control={control}
          labelText="Brukte du noe beskyttelsesutstyr på skadetidspunktet? *"
          options={jaNeiOptions}
          error={errors.f23_beskyttelsesutstyr}
          type="radio"
          variant="column"
          tracking={false}
        />
        <Input
          name="f24_skadeHvor"
          labelText="Hvor skjedde skaden, navn på arena/sted? *"
          control={control}
          error={errors.f24_skadeHvor}
          tracking={false}
        />
        <Textarea
          name="f25_annet"
          labelText="Her kan du gi oss andre opplysninger du mener vi har behov for å vite"
          control={control}
          error={errors.f25_annet}
          tracking={false}
          maxWarning={500}
        />
      </Box>

      <Alert severity="informative" variant="outlined" className="builders-margin-bottom-60">
        <Title as="h5" size="6">
          Samtykke
        </Title>
        <Text>{texts.skademeldingIdrettsskadeSamtykke}</Text>
        <Input
          name="f26_samtykke"
          control={control}
          labelText="Jeg samtykker *"
          type="checkbox"
          error={errors.f26_samtykke}
          tracking={false}
        />
      </Alert>
      <Button
        variant="critical"
        type="submit"
        loading={isSubmitting}
        onClick={handleSubmit(onSubmit)}
      >
        Send inn
      </Button>
      {success && (
        <Alert severity="success" className="sending-alert">
          <Text>Skjema sendt</Text>
        </Alert>
      )}
      {errorSubmitting && (
        <Alert severity="error" className="sending-alert">
          <Text>Sending av skjema feilet, prøv igjen senere</Text>
        </Alert>
      )}
    </form>
  );
};

export default SportInjuryForm;
